import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getMarketplacePaymentStatus } from "../../api/myboothshareBilling";
import { useAuth } from "../../context/AuthContext";
import MarketplaceShell from "../marketplace/MarketplaceShell";
import {
  formatMarketplaceDateTime,
  formatMarketplaceMoneyFromCents,
  getMarketplaceBookingLabel,
  getMarketplaceReleaseLabel,
} from "../../utils/myboothshareBillingPresentation";

export default function CheckoutSuccessPage() {
  const { token } = useAuth();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);

  const bookingId = Number(params.get("booking_id") || 0);
  const sessionId = params.get("session_id") || null;

  useEffect(() => {
    if (!bookingId) {
      setLoading(false);
      setError("Missing rental reference.");
      return;
    }
    let active = true;
    setLoading(true);
    setError("");
    getMarketplacePaymentStatus(bookingId, {
      token: token || null,
      sessionId,
    })
      .then((data) => {
        if (active) setStatus(data);
      })
      .catch((err) => {
        if (active) {
          setError(
            err?.response?.data?.detail ||
              err.message ||
              "Unable to load rental payment status."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [bookingId, sessionId, token]);

  return (
    <MarketplaceShell>
      <section className="mx-auto max-w-4xl space-y-6">
        <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="bg-[#1f6f52] px-6 py-8 text-white">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-100">
              Checkout Complete
            </p>
            <h1 className="mt-3 text-3xl font-semibold">Payment received</h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-emerald-50 md:text-base">
              Your rental payment was submitted successfully. Ellyanna Boothshare will
              keep tracking the agreement and payout timeline after the rental ends.
            </p>
          </div>
          <div className="space-y-5 px-6 py-6">
            {loading ? <p className="text-sm text-slate-600">Loading payment status...</p> : null}
            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
            {status ? (
              <>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-[1.35rem] border border-slate-200 bg-slate-50 p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Rental Agreement
                    </div>
                    <div className="mt-2 text-xl font-semibold text-slate-950">
                      #{status.booking_id}
                    </div>
                  </div>
                  <div className="rounded-[1.35rem] border border-slate-200 bg-slate-50 p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Paid by renter
                    </div>
                    <div className="mt-2 text-xl font-semibold text-slate-950">
                      {formatMarketplaceMoneyFromCents(status.amount_cents, status.currency)}
                    </div>
                  </div>
                  <div className="rounded-[1.35rem] border border-slate-200 bg-slate-50 p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Agreement status
                    </div>
                    <div className="mt-2 text-xl font-semibold text-slate-950">
                      {getMarketplaceBookingLabel(status.booking_status)}
                    </div>
                  </div>
                  <div className="rounded-[1.35rem] border border-slate-200 bg-slate-50 p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Release status
                    </div>
                    <div className="mt-2 text-xl font-semibold text-slate-950">
                      {getMarketplaceReleaseLabel(status)}
                    </div>
                  </div>
                </div>
                <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Payment status
                      </div>
                      <div className="mt-2 text-base font-semibold text-slate-900">
                        {getMarketplaceBookingLabel(status.payment_status)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Estimated release
                      </div>
                      <div className="mt-2 text-base font-semibold text-slate-900">
                        {status.estimated_release_at_utc
                          ? formatMarketplaceDateTime(status.estimated_release_at_utc)
                          : "Pending rental completion"}
                      </div>
                    </div>
                  </div>
                </div>
                {status.dispute_open ? (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    This rental agreement currently has an open dispute, so the release remains paused
                    while the case is reviewed.
                  </div>
                ) : null}
              </>
            ) : null}
            <div className="flex flex-wrap gap-3">
              <Link
                to="/account/rentals"
                className="rounded-full bg-[#2f3566] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#252a53]"
              >
                View my rentals
              </Link>
              <Link
                to="/search"
                className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-slate-400"
              >
                Browse more workspaces
              </Link>
            </div>
          </div>
        </article>
      </section>
    </MarketplaceShell>
  );
}
